<template>
  <div class="date-input-wrapper">
    <div
      :class="selectClasses"
      @click="handleWrapperClick"
      :tabindex="isDisabled ? -1 : 0"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.esc="closeDropdown"
    >
      <input
        v-if="type === 'input'"
        ref="input"
        :disabled="isDisabled"
        :value="rawInput"
        type="text"
        :placeholder="!displayValue ? placeholder : ''"
        class="date-input__input"
        @blur="handleBlur"
        @keydown.tab="closeDropdown"
      />
      <span v-else class="date-input__value" :class="{ 'date-input__value--placeholder': !displayValue }">
        {{ displayValue || placeholder }}
      </span>
      <CalendarIcon
        class="date-input__icon"
        :color="iconColor"
      />
    </div>

    <transition name="dropdown">
      <div v-if="isOpen" class="date-input__dropdown" :style="dropdownStyle" @mousedown.prevent>
        <CalendarDate
          v-model="internalDate"
          :size="size"
          :value="value"
          :format="format"
          :default-display-date="defaultDisplayDate"
          :range="range"
          :events="events"
          :holidays="holidays"
          :disabled-dates="disabledDates"
          :available-dates="availableDates"
          :disabled-past-dates="disabledPastDates"
          :min-date="minDate"
          :disabled-future-dates="disabledFutureDates"
          :start-year="startYear"
          :end-year="endYear"
          @date-selected="selectDate"
          @range-selected="selectDate"
        />
      </div>
    </transition>

    <div v-if="hasError" class="date-input--error">
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import Cleave from 'cleave.js';
import { clsx } from 'clsx';
import moment from 'moment';
import { CalendarDate } from '@/shared/ui/calendar';
import { CalendarIcon } from '@/shared/ui/icons';
import { EventBus } from '@/shared/lib/event-bus';

export default {
  components: { CalendarIcon, CalendarDate },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    size: {
      type: String,
      default: 'small',
      validator: (value) => ['normal', 'small'].includes(value)
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      enum: ['select', 'input'],
      default: 'select',
    },
    value: {
      type: [String, Date, Object, Array],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Select date',
    },
    format: {
      type: String,
      default: 'DD.MM.YYYY',
    },
    defaultDisplayDate: {
      type: [String, Date, Object],
      default: null,
    },
    range: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => [],
      validator: (value) => value.every((event) => event.date),
    },
    holidays: {
      type: Array,
      default: () => [],
      validator: (value) => value.every((holiday) => holiday.date),
    },
    disabledDates: {
      type: Array,
      default: () => [],
      validator: (value) => value.every((item) => {
        return item && (typeof item === 'string' || item instanceof Date || (typeof item === 'object' && item.date));
      }),
    },
    availableDates: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((item) => {
        return item && (typeof item === 'string' || item instanceof Date || (typeof item === 'object' && item.date));
      }),
    },
    disabledPastDates: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: [String, Date, Object],
      default: null,
    },
    disabledFutureDates: {
      type: Boolean,
      default: false,
    },
    startYear: {
      type: Number,
      default: null,
    },
    endYear: {
      type: Number,
      default: null,
    },
    iconColor: {
      type: String,
      default: '#71717A',
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
      dropdownStyle: {},
      focusedIndex: -1,
      rawInput: '',
      internalDate: null,
      cleaveInstance: null,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('scroll', this.handleScrollOutside, true);
    EventBus.$on('selector:opened', this.closeDropdown);

    if (this.value) {
      if (this.range) {
        if (Array.isArray(this.value) && this.value.length > 0) {
          this.internalDate = this.value;
          if (this.type === 'input') this.updateRawInput(this.value);
        }
      } else {
        const parsedDate = typeof this.value === 'string'
          ? moment(this.value, this.format)
          : moment(this.value);

        if (parsedDate.isValid()) {
          this.internalDate = parsedDate.toDate();
          if (this.type === 'input') this.rawInput = parsedDate.format(this.format);
        }
      }
    }

    this.$nextTick(this.initCleave);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('scroll', this.handleScrollOutside, true);
    EventBus.$off('selector:opened', this.closeDropdown);
    if (this.cleaveInstance) {
      this.cleaveInstance.destroy();
    }
  },
  computed: {
    selectClasses() {
      return clsx('date-input', {
        'date-input--disabled': this.isDisabled,
        'date-input--has-error': this.hasError,
        'date-input--open': this.isOpen,
        'date-input--input': this.type === 'input'
      });
    },
    displayValue() {
      if (!this.value) return '';

      if (this.range) {
        let startDate, endDate;

        if (Array.isArray(this.value) && this.value.length > 0) {
          startDate = moment(this.value[0]);
          endDate = moment(this.value[1]);
        } else if (this.value && typeof this.value === 'object') {
          startDate = moment(this.value.startDate);
          endDate = moment(this.value.endDate);
        } else {
          return '';
        }

        let display = '';
        if (startDate && startDate.isValid()) {
          display += startDate.format(this.format);
        }
        if (endDate && endDate.isValid()) {
          display += display ? ` - ${endDate.format(this.format)}` : endDate.format(this.format);
        }
        return display;
      }

      const date = moment(this.value, this.format);
      if (!date.isValid()) return '';
      return date.format(this.format);
    },
    hasError() {
      return this.errorMessages?.length > 0;
    },
  },
  methods: {
    updateRawInput(value) {
      if (!value) {
        this.rawInput = '';
        return;
      }

      if (this.range && Array.isArray(value)) {
        const startDate = value[0] ? moment(value[0]) : null;
        const endDate = value[1] ? moment(value[1]) : null;

        let display = '';
        if (startDate && startDate.isValid()) {
          display += startDate.format(this.format);
        }
        if (endDate && endDate.isValid()) {
          display += display ? ` - ${endDate.format(this.format)}` : endDate.format(this.format);
        }
        this.rawInput = display;
      } else {
        this.rawInput = moment(value, this.format).format(this.format);
      }
    },
    initCleave() {
      if (this.type !== 'input' || !this.$refs.input) return;

      if (this.cleaveInstance) {
        this.cleaveInstance.destroy();
        this.cleaveInstance = null;
      }

      const valueBeforeInit = this.rawInput;
      let isInitializing = true;

      const onValueChanged = (e) => {
        if (isInitializing) return;

        this.rawInput = e.target.value;
        if (!this.range) {
          this.handleInputChange();
        }
      };

      if (this.range) {
        const dateDelimiter = this.format.match(/[^A-Z]/)?.[0] || '.';
        this.cleaveInstance = new Cleave(this.$refs.input, {
          blocks: [2, 2, 4, 2, 2, 4],
          delimiters: [dateDelimiter, dateDelimiter, ' - ', dateDelimiter, dateDelimiter],
          onValueChanged,
        });
      } else {
        this.cleaveInstance = new Cleave(this.$refs.input, {
          date: true,
          delimiter: '.',
          datePattern: ['d', 'm', 'Y'],
          onValueChanged,
        });
      }

      if (valueBeforeInit) {
        this.cleaveInstance.setRawValue(valueBeforeInit);
      }

      this.$nextTick(() => {
        isInitializing = false;
      });
    },
    handleInputChange() {
      if (this.range) {
        return;
      }

      if (!this.rawInput) {
        this.internalDate = null;
        this.$emit('input', this.internalDate);
        this.$emit('change', this.internalDate);
        return;
      }

      const parsedDate = moment(this.rawInput, this.format, true);
      if (parsedDate.isValid()) {
        this.internalDate = parsedDate.toDate();
        this.$emit('input', this.internalDate);
        this.$emit('change', this.internalDate);
      }
    },
    parsePartialDate(dateString) {
      if (!dateString || typeof dateString !== 'string') return null;

      const cleanString = dateString.replace(/\D/g, '');

      if (cleanString.length === 0) return null;
      let day, month, year;

      if (this.format.includes('DD.MM.YYYY')) {
        switch (cleanString.length) {
          case 1:
          case 2:
            day = parseInt(cleanString.padEnd(2, '0'));
            month = 1;
            year = new Date().getFullYear();
            break;
          case 3:
          case 4:
            day = parseInt(cleanString.substring(0, 2));
            month = parseInt(cleanString.substring(2).padEnd(2, '0'));
            year = new Date().getFullYear();
            break;
          case 5:
          case 6:
          case 7:
          case 8:
            day = parseInt(cleanString.substring(0, 2));
            month = parseInt(cleanString.substring(2, 4));
            year = parseInt(cleanString.substring(4).padEnd(4, '0'));
            if (year < 100) year += 2000;
            if (year < 1900) year = new Date().getFullYear();
            break;
          default:
            return null;
        }
      } else {
        return moment(dateString, this.format, false);
      }

      if (day < 1 || day > 31 || month < 1 || month > 12) {
        return null;
      }

      const parsedDate = moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, 'YYYY-MM-DD', true);
      return parsedDate.isValid() ? parsedDate : null;
    },
    findNearestAvailableDate(date) {
      if (!date || !date.isValid()) return null;

      if (!this.isDateDisabled(date)) {
        return date;
      }

      let forwardDate = date.clone();
      let backwardDate = date.clone();
      let daysChecked = 0;
      const maxDaysToCheck = 365 * 5;

      while (daysChecked < maxDaysToCheck) {
        forwardDate.add(1, 'day');
        if (!this.isDateDisabled(forwardDate)) {
          return forwardDate;
        }

        backwardDate.subtract(1, 'day');
        if (!this.isDateDisabled(backwardDate)) {
          return backwardDate;
        }

        daysChecked += 2;
      }

      return null;
    },
    isDateDisabled(date) {
      if (!date || !date.isValid()) return true;

      const today = moment().startOf('day');

      if (this.disabledPastDates && date.isBefore(today, 'day')) {
        return true;
      }

      if (this.minDate) {
        const minMoment = moment(this.minDate);
        if (minMoment.isValid() && date.isBefore(minMoment, 'day')) {
          return true;
        }
      }

      if (this.disabledFutureDates && date.isAfter(today, 'day')) {
        return true;
      }

      if (this.disabledDates && this.disabledDates.length > 0) {
        const isExplicitlyDisabled = this.disabledDates.some(disabledDate => {
          const disabledMoment = moment(disabledDate.date || disabledDate);
          return disabledMoment.isValid() && date.isSame(disabledMoment, 'day');
        });
        if (isExplicitlyDisabled) {
          return true;
        }
      }

      return false;
    },
    handleBlur() {
      this.$emit('blur');
      if (!this.rawInput) {
        this.$emit('input', null);
        this.internalDate = null;
        return;
      }

      if (this.range) {
        const parts = this.rawInput.split(' - ').map(s => s.trim()).filter(s => s.length > 0);
        let startDate = null;
        let endDate = null;
        let isValidRange = false;

        if (parts.length === 1) {
          const parsedDate = moment(parts[0], this.format, true);
          if (parsedDate.isValid()) {
            const availableDate = this.findNearestAvailableDate(parsedDate);
            if (availableDate) {
              startDate = availableDate.toDate();
              endDate = availableDate.toDate();
              isValidRange = true;
            }
          } else {
            const partialDate = this.parsePartialDate(parts[0]);
            if (partialDate) {
              const availableDate = this.findNearestAvailableDate(partialDate);
              if (availableDate) {
                startDate = availableDate.toDate();
                endDate = availableDate.toDate();
                isValidRange = true;
              }
            }
          }
        } else if (parts.length === 2) {
          const parsedStart = this.parsePartialDate(parts[0]) || moment(parts[0], this.format, true);
          const parsedEnd = this.parsePartialDate(parts[1]) || moment(parts[1], this.format, true);

          if (parsedStart.isValid() && parsedEnd.isValid()) {
            const availableStart = this.findNearestAvailableDate(parsedStart);
            const availableEnd = this.findNearestAvailableDate(parsedEnd);

            if (availableStart && availableEnd) {
              isValidRange = true;
              if (availableStart.isAfter(availableEnd)) {
                [startDate, endDate] = [availableEnd.toDate(), availableStart.toDate()];
              } else {
                [startDate, endDate] = [availableStart.toDate(), availableEnd.toDate()];
              }
            }
          } else if (parsedStart.isValid() && !parts[1]) {
            const availableStart = this.findNearestAvailableDate(parsedStart);
            if (availableStart) {
              startDate = availableStart.toDate();
              isValidRange = true;
            }
          } else if (!parts[0] && parsedEnd.isValid()) {
            const availableEnd = this.findNearestAvailableDate(parsedEnd);
            if (availableEnd) {
              endDate = availableEnd.toDate();
              isValidRange = true;
            }
          }
        }

        const newValue = (startDate || endDate) ? [startDate, endDate] : null;

        if (isValidRange) {
          this.internalDate = newValue;
          this.$emit('input', newValue);
          this.$emit('change', newValue);
          this.$nextTick(() => {
            this.updateRawInput(newValue);
          });
        } else {
          this.rawInput = '';
          this.internalDate = null;
          this.$emit('input', null);
          this.$emit('change', null);
        }
      } else {
        const parsedDate = moment(this.rawInput, this.format, true);

        if (!parsedDate.isValid()) {
          const partialDate = this.parsePartialDate(this.rawInput);
          if (partialDate) {
            const availableDate = this.findNearestAvailableDate(partialDate);
            if (availableDate) {
              const formattedValue = availableDate.toDate();
              this.internalDate = formattedValue;
              this.$emit('input', formattedValue);
              this.$emit('change', formattedValue);
              this.rawInput = availableDate.format(this.format);
            } else {
              this.rawInput = '';
              this.internalDate = null;
              this.$emit('input', null);
              this.$emit('change', null);
            }
          } else {
            this.rawInput = '';
            this.internalDate = null;
            this.$emit('input', null);
            this.$emit('change', null);
          }
        } else {
          const availableDate = this.findNearestAvailableDate(parsedDate);
          if (availableDate) {
            const formattedValue = availableDate.toDate();
            this.internalDate = formattedValue;
            this.$emit('input', formattedValue);
            this.$emit('change', formattedValue);
            this.rawInput = availableDate.format(this.format);
          } else {
            this.rawInput = '';
            this.internalDate = null;
            this.$emit('input', null);
            this.$emit('change', null);
          }
        }
      }
    },
    handleWrapperClick(event) {
      if (this.isDisabled) return;

      if (this.type === 'input' && this.$refs.input && this.$refs.input === event.target) {
        this.openDropdown();
      } else {
        this.toggleDropdown();
      }
    },
    toggleDropdown() {
      if (this.isDisabled) return;
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    openDropdown() {
      if (this.isDisabled) return;
      const rect = this.$el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const style = {
        position: 'fixed',
        left: rect.left + 'px',
        width: rect.width + 'px',
        zIndex: 2000,
      };
      if (spaceBelow >= 320) {
        style.top = (rect.bottom + 4) + 'px';
      } else {
        style.bottom = (window.innerHeight - rect.top + 4) + 'px';
      }
      this.dropdownStyle = style;
      EventBus.$emit('selector:opened');
      this.$nextTick(() => {
        this.isOpen = true;
      });
      if (this.type === 'input') {
        this.$nextTick(() => {
          this.$refs.input?.focus();
        });
      }
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
    },
    selectDate(newValue) {
      if (!this.range) {
        const incoming = moment(newValue);
        const current = this.value ? moment(this.value) : null;

        if (current && incoming.isValid() && current.isValid() && incoming.isSame(current, 'day')) {
          this.closeDropdown();
          return;
        }
      }

      let emittedValue = newValue;

      if (this.range) {
        let startDate = null;
        let endDate = null;

        if (Array.isArray(newValue)) {
          startDate = newValue[0];
          endDate = newValue[1];
          emittedValue = newValue;
        } else if (newValue && typeof newValue === 'object' && newValue.startDate) {
          startDate = newValue.startDate;
          endDate = newValue.endDate;
          emittedValue = [startDate, endDate];
        }

        this.internalDate = emittedValue;

        if (startDate && endDate) {
          this.closeDropdown();
        }

      } else {
        this.internalDate = newValue;
        this.closeDropdown();
      }

      this.$emit('input', emittedValue);
      this.$emit('change', emittedValue);
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    handleScrollOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
  },
  watch: {
    range() {
      this.$nextTick(this.initCleave);
    },
    value(newVal) {
      if (this.range) {
        if (Array.isArray(newVal) && newVal.length > 0) {
          this.internalDate = newVal;
          this.updateRawInput(newVal);
        } else if (newVal && typeof newVal === 'object' && newVal.startDate) {
          this.internalDate = [newVal.startDate, newVal.endDate];
          this.updateRawInput([newVal.startDate, newVal.endDate]);
        } else {
          this.internalDate = null;
          this.rawInput = '';
        }
      } else {
        if (newVal) {
          let parsedDate;
          if (typeof newVal === 'string') {
            parsedDate = moment(newVal, this.format, true);
            if (!parsedDate.isValid()) {
              parsedDate = moment(newVal);
            }
          } else {
            parsedDate = moment(newVal);
          }

          if (parsedDate.isValid()) {
            this.internalDate = parsedDate.toDate();
            this.rawInput = parsedDate.format(this.format);
          } else {
            this.internalDate = null;
            this.rawInput = '';
          }
        } else {
          this.internalDate = null;
          this.rawInput = '';
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.date-input-wrapper {
  position: relative;
  width: 100%;
}

.date-input {
  position: relative;
  background-color: #ffffff;
  border: 1px solid #E4E4E7;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 4px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--disabled {
    background-color: #F6F6F9;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--has-error {
    border-color: #f00;
  }

  &__input {
    font-family: inherit;
    font-size: 14px;
    color: #09090B;
    background: transparent;
    border: none !important;
    outline: none !important;
    flex: 1;
    cursor: text;

    &::placeholder {
      color: #D4D4D8;
    }
  }

  &__value {
    flex: 1;
    color: #09090B;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--placeholder {
      color: #D4D4D8;
    }
  }

  &__icon {
    margin-left: 8px;
    flex-shrink: 0;
  }

  &__dropdown {
    position: absolute;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    width: 100%;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f3f4f6;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  &--error {
    font-family: 'Geist', sans-serif;
    font-size: 12px;
    color: #f00;
    margin-top: 4px;
    margin-bottom: 12px;
  }
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

::v-deep .calendar {
  margin: 4px;
  width: auto !important;
}
</style>
